import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Trash2, Save, Bell, Pencil, BookOpen, CheckCircle2 } from 'lucide-react';
import { getGlobalNotifications, getTutorialCommentsAdmin, moderateTutorialComment, deleteTutorialComment } from '../../redux/api';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { generateFeatureTimeline } from '../../data/featureTimeline';
import { getRoutePath } from '../../utils/routingUtils';

const LINK_MARKDOWN = /\[([^\]]+)\]\(([^)]+)\)/g;

const NotificationMessage = ({ message }) => {
  const location = useLocation();
  const nodes = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(LINK_MARKDOWN.source, 'g');

  while ((match = re.exec(message || '')) !== null) {
    if (match.index > lastIndex) {
      nodes.push(message.slice(lastIndex, match.index));
    }
    const label = match[1];
    const target = match[2];
    const href = /^https?:\/\//i.test(target)
      ? target
      : getRoutePath(target, location.pathname);
    nodes.push(
      <Link
        key={`${href}-${match.index}`}
        to={href}
        onClick={(e) => e.stopPropagation()}
        className="text-blue-400 underline underline-offset-2 hover:text-blue-300"
      >
        {label}
      </Link>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < (message || '').length) {
    nodes.push(message.slice(lastIndex));
  }

  return (
    <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">
      {nodes.length ? nodes : message}
    </p>
  );
};

const createEmptyEntry = (type = 'note') => ({
  id: `${type}-${Date.now()}`,
  title: '',
  content: '',
  type,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

const TAB_TO_TYPE = {
  notes: 'note',
  notifications: 'notification'
};

const NotificationsView = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { socket, isConnected } = useWebSocket();
  const userId = user?.result?._id || 'anonymous';
  const storageKey = useMemo(() => `spinTasker_notes_${userId}`, [userId]);

  const [entries, setEntries] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [activeTab, setActiveTab] = useState('notifications');
  const [draft, setDraft] = useState(() => createEmptyEntry('note'));
  const featureTimeline = useMemo(() => generateFeatureTimeline(), []);
  const [globalNotifications, setGlobalNotifications] = useState(featureTimeline);
  const [loadingGlobalNotifications, setLoadingGlobalNotifications] = useState(false);
  const readStorageKey = useMemo(() => `spinTasker_notification_read_${userId}`, [userId]);
  const hiddenStorageKey = useMemo(() => `spinTasker_notification_hidden_${userId}`, [userId]);
  const [readNotifications, setReadNotifications] = useState({}); // { [notificationId]: true }
  const [hiddenNotifications, setHiddenNotifications] = useState({}); // { [notificationId]: true }
  const [tutorialAdmin, setTutorialAdmin] = useState({ pending: [], approved: [], rejected: [] });
  const [loadingTutorialAdmin, setLoadingTutorialAdmin] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const normalised = parsed.map((item) => ({
            ...item,
            type: item.type || 'note'
          }));
          setEntries(normalised);
        }
      }
    } catch (error) {
      console.error('Failed to load notes from storage:', error);
      toast.error('Ne mogu da učitam beleške iz lokalne memorije');
    }
  }, [storageKey]);

  // Load per-user read notification state
  useEffect(() => {
    try {
      const stored = localStorage.getItem(readStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setReadNotifications(parsed);
        }
      }
    } catch (e) {
      // ignore
    }
  }, [readStorageKey]);

  // Load hidden notifications
  useEffect(() => {
    try {
      const stored = localStorage.getItem(hiddenStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setHiddenNotifications(parsed);
        }
      }
    } catch (e) {
      // ignore
    }
  }, [hiddenStorageKey]);

  // Fetch global notifications when notifications tab is active
  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchGlobalNotifications();
    }
  }, [activeTab]);

  const isSuperAdmin = user?.result?.role === 'superadmin';

  const fetchTutorialCommentsAdmin = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setLoadingTutorialAdmin(true);
      const response = await getTutorialCommentsAdmin();
      const d = response.data || {};
      setTutorialAdmin({
        pending: Array.isArray(d.pending) ? d.pending : [],
        approved: Array.isArray(d.approved) ? d.approved : [],
        rejected: Array.isArray(d.rejected) ? d.rejected : []
      });
    } catch (error) {
      toast.error('Ne mogu da učitam komentare sa tutorijala.');
    } finally {
      setLoadingTutorialAdmin(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (activeTab === 'comments' && isSuperAdmin) {
      fetchTutorialCommentsAdmin();
    }
  }, [activeTab, isSuperAdmin, fetchTutorialCommentsAdmin]);

  const handleModerateTutorialComment = async (commentId, action) => {
    try {
      await moderateTutorialComment(commentId, { action });
      toast.success(action === 'approve' ? 'Komentar je odobren.' : 'Komentar je odbijen.');
      fetchTutorialCommentsAdmin();
    } catch (error) {
      toast.error('Greška pri moderaciji komentara.');
    }
  };

  const handleDeleteTutorialComment = async (commentId) => {
    if (!window.confirm('Da li želite trajno da obrišete ovaj komentar?')) return;
    try {
      await deleteTutorialComment(commentId);
      toast.success('Komentar je obrisan.');
      fetchTutorialCommentsAdmin();
    } catch (error) {
      toast.error('Brisanje komentara nije uspelo.');
    }
  };

  // Listen for new global notifications via WebSocket
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (data) => {
      console.log('📢 Received new global notification:', data);
      // Add new notification to the list if not already present
      setGlobalNotifications(prev => {
        const exists = prev.some(n => n._id === data.notification._id);
        if (!exists) {
          toast.info(`Novo obaveštenje: ${data.notification.title}`);
          const incoming = {
            _id: data.notification._id,
            title: data.notification.title || 'Obaveštenje',
            message: data.notification.message || '',
            type: data.notification.type || 'info',
            createdAt: data.notification.createdAt || new Date().toISOString()
          };
          return [incoming, ...prev].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }
        return prev;
      });
    };

    socket.on('new_global_notification', handleNewNotification);

    return () => {
      socket.off('new_global_notification', handleNewNotification);
    };
  }, [socket, isConnected]);

  const fetchGlobalNotifications = async () => {
    try {
      setLoadingGlobalNotifications(true);
      const response = await getGlobalNotifications();
      const remoteNotifications = Array.isArray(response.data) ? response.data : [];
      const combinedMap = new Map();

      featureTimeline.forEach((item) => {
        combinedMap.set(item._id, item);
      });

      remoteNotifications.forEach((item) => {
        if (!item || !item._id) {
          return;
        }
        const title = String(item.title || '').trim().toLowerCase();
        const message = String(item.message || '');
        // Staro obaveštenje: "Da li ste znali da se logo... primljenih poziva"
        if (title === 'logo' && /da li ste znali|primljenih poziva/i.test(message)) {
          return;
        }
        if (title === 'pretplata' && /20 evra|nove godine/i.test(message)) {
          return;
        }
        if (title === 'dodavanje radnika' && /^update\s*$/i.test(message.trim())) {
          return;
        }
        if (title === 'finansijski modul') {
          return;
        }
        if (title === 'tema i izgled aplikacije') {
          return;
        }
        if (title === 'automatsko geokodiranje adresa') {
          return;
        }
        if (title === 'fakture i virman') {
          return;
        }
        if (title === 'video uputstva u aplikaciji') {
          return;
        }
        combinedMap.set(item._id, {
          _id: item._id,
          title: item.title || item.message || 'Obaveštenje',
          message: item.message || '',
          type: item.type || 'info',
          category: item.category || null,
          createdAt: item.createdAt || new Date().toISOString()
        });
      });

      const merged = Array.from(combinedMap.values()).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setGlobalNotifications(merged);
    } catch (error) {
      console.error('Error fetching global notifications:', error);
      // Don't show error toast as this is background fetch
    } finally {
      setLoadingGlobalNotifications(false);
    }
  };

  const persistEntries = (updatedEntries) => {
    setEntries(updatedEntries);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updatedEntries));
    } catch (error) {
      console.error('Failed to persist notes:', error);
      toast.error('Ne mogu da sačuvam beleške u lokalnu memoriju');
    }
  };

  const persistRead = (updated) => {
    setReadNotifications(updated);
    try {
      localStorage.setItem(readStorageKey, JSON.stringify(updated));
    } catch (e) {
      // ignore
    }
  };

  const markNotificationAsRead = (notifId) => {
    const updated = { ...readNotifications, [notifId]: true };
    persistRead(updated);
  };

  const unreadNotificationCount = useMemo(
    () => globalNotifications.filter((n) => !hiddenNotifications[n._id] && !readNotifications[n._id]).length,
    [globalNotifications, hiddenNotifications, readNotifications]
  );

  const persistHidden = (updated) => {
    setHiddenNotifications(updated);
    try {
      localStorage.setItem(hiddenStorageKey, JSON.stringify(updated));
    } catch (e) {
      // ignore
    }
  };

  // Track if we're in "create mode" (new entry being created)
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Reset form when tab changes
  useEffect(() => {
    const activeType = TAB_TO_TYPE[activeTab];
    
    // Always reset form state when tab changes
    setSelectedEntryId(null);
    setIsCreatingNew(false);
    
    // If switching to notifications or comments tab, don't show form
    if (activeTab === 'notifications' || activeTab === 'comments') {
      setDraft(createEmptyEntry('notification')); // Set empty draft but don't show form
      return;
    }
    
    // For notes tab, only auto-select first entry if there are entries
    if (activeTab === 'notes') {
      const first = entries.find((entry) => (entry.type || 'note') === 'note');
      if (first) {
        // Don't auto-select, just clear form - user can click to select
        setDraft(createEmptyEntry('note'));
      } else {
        // No entries, prepare empty form
        setDraft(createEmptyEntry('note'));
      }
    }
  }, [activeTab]); // Reset when tab changes

  const handleCreate = () => {
    const type = TAB_TO_TYPE[activeTab];
    
    // If there's unsaved content in draft, save it first
    const hasUnsavedChanges = (draft.title.trim() || draft.content.trim()) && 
                              !entries.some((note) => note.id === draft.id) &&
                              isCreatingNew;
    
    if (hasUnsavedChanges) {
      // Save current draft first, then create new one
      handleSave();
      // Wait a bit for save to complete, then create new
      setTimeout(() => {
        const newEntry = createEmptyEntry(type);
        setSelectedEntryId(null);
        setDraft(newEntry);
        setIsCreatingNew(true);
      }, 200);
    } else {
      // Just create new empty draft
      const newEntry = createEmptyEntry(type);
      setSelectedEntryId(null);
      setDraft(newEntry);
      setIsCreatingNew(true);
    }
  };

  const handleCancel = () => {
    // Cancel creating new entry - close form
    setSelectedEntryId(null);
    setIsCreatingNew(false);
    
    // If there are entries, select first one, otherwise just hide form
    const activeType = TAB_TO_TYPE[activeTab];
    const first = entries.find((entry) => (entry.type || 'note') === activeType);
    if (first) {
      setSelectedEntryId(first.id);
      setDraft({ ...first });
    }
  };

  const handleSelect = (note) => {
    setSelectedEntryId(note.id);
    setDraft({ ...note }); // Create copy to avoid mutations
    setIsCreatingNew(false);
  };

  const handleDelete = (id) => {
    // Ask for confirmation before deleting
    const noteToDelete = entries.find((note) => note.id === id);
    const noteTitle = noteToDelete?.title || 'ova beleška';
    
    if (!window.confirm(`Da li ste sigurni da želite da obrišete "${noteTitle}"?\n\nOva akcija se ne može poništiti.`)) {
      return; // User cancelled
    }
    
    const updated = entries.filter((note) => note.id !== id);
    persistEntries(updated);
    if (selectedEntryId === id) {
      const activeType = TAB_TO_TYPE[activeTab];
      const firstSameType = updated.find((entry) => (entry.type || 'note') === activeType);
      if (firstSameType) {
        setSelectedEntryId(firstSameType.id);
        setDraft({ ...firstSameType });
        setIsCreatingNew(false);
      } else {
        // No more entries, hide form
        setSelectedEntryId(null);
        setIsCreatingNew(false);
      }
    }
    toast.success('Beleška je obrisana');
  };

  const handleFieldChange = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString()
    }));
  };

  const handleSave = () => {
    if (!draft.title.trim() && !draft.content.trim()) {
      toast.error('Unesite naslov ili sadržaj beleške pre čuvanja');
      return;
    }

    // Make sure draft has proper timestamp
    const draftToSave = {
      ...draft,
      updatedAt: new Date().toISOString(),
      createdAt: draft.createdAt || new Date().toISOString()
    };

    // Check if this entry already exists in the list
    const existingIndex = entries.findIndex((note) => note.id === draftToSave.id);
    const isEditingExisting = existingIndex >= 0;
    
    let updatedEntries;
    if (isEditingExisting) {
      // Update existing entry in place (keep same position)
      updatedEntries = entries.map((note, index) => 
        index === existingIndex ? draftToSave : note
      );
    } else {
      // Add new entry to the beginning of the list
      updatedEntries = [draftToSave, ...entries];
    }

    persistEntries(updatedEntries);
    
    // Always close form after save (both for editing and creating new)
    setSelectedEntryId(null);
    setIsCreatingNew(false);
    
    toast.success('Beleška je sačuvana');
  };

  const formatTimestamp = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('sr-RS', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredEntries = useMemo(() => {
    const activeType = TAB_TO_TYPE[activeTab];
    return entries
      .filter((entry) => (entry.type || 'note') === activeType)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [entries, activeTab]);

  return (
    <div className="bg-gray-900 min-h-screen p-3 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-white">{t('notifications.titleWithNotes')}</h1>
            <p className="text-sm text-gray-400">Upravljajte internim beleškama i pregledajte ključna obaveštenja iz sistema.</p>
          </div>
          <div className="inline-flex flex-wrap rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'notifications' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('notifications')}
            >
              Obaveštenja
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'notes' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
              onClick={() => setActiveTab('notes')}
            >
              Beleške
            </button>
            {isSuperAdmin && (
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'comments' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
                }`}
                onClick={() => setActiveTab('comments')}
              >
                Komentari
              </button>
            )}
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          <aside
            className={`${
              activeTab === 'notifications' || activeTab === 'comments' ? 'lg:col-span-12' : 'lg:col-span-4'
            } bg-gray-800/80 border border-gray-700 rounded-xl shadow-lg flex flex-col`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {activeTab === 'notes'
                    ? 'Beleške'
                    : activeTab === 'comments'
                      ? 'Komentari (stranica Tutorijali)'
                    : 'Obaveštenja od sistema'}
                </h2>
                <p className="text-xs text-gray-400">
                  {activeTab === 'notes'
                    ? 'Brzo zabeležite podatke i ideje'
                    : activeTab === 'comments'
                      ? 'Moderacija komentara sa javne stranice /tutorials'
                    : 'Hronološki vodič kroz funkcionalnosti — od septembra 2025.'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {activeTab === 'notifications' && unreadNotificationCount > 0 && (
                  <>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
                      {unreadNotificationCount} novo
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = { ...readNotifications };
                        globalNotifications.forEach((n) => {
                          if (!hiddenNotifications[n._id]) {
                            updated[n._id] = true;
                          }
                        });
                        persistRead(updated);
                        toast.success('Sva obaveštenja su označena kao pročitana');
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
                    >
                      <CheckCircle2 size={14} />
                      Pročitaj sve
                    </button>
                  </>
                )}
                {activeTab === 'notes' && (
                  <button
                    onClick={handleCreate}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Plus size={16} />
                    Novo
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {activeTab === 'notifications' ? (
                // Show global notifications from server
                <>
                  {loadingGlobalNotifications ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : globalNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-10 px-6">
                      <p className="text-sm">Nema obaveštenja od sistema.</p>
                      <p className="text-xs text-gray-500 mt-1">Obaveštenja će se prikazati ovde kada budu poslata.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-700">
                      {globalNotifications
                        .filter(n => !hiddenNotifications[n._id])
                        .map((notification) => {
                          const notifId = notification._id;
                          const isSelected = selectedEntryId === `global-${notifId}`;
                          const isRead = !!readNotifications[notifId];

                          const handleToggle = () => {
                            if (isSelected) {
                              setSelectedEntryId(null);
                            } else {
                              setSelectedEntryId(`global-${notifId}`);
                              setDraft({
                                id: `global-${notifId}`,
                                title: notification.title,
                                content: notification.message,
                                type: 'notification',
                                createdAt: notification.createdAt,
                                updatedAt: notification.createdAt
                              });
                              if (!readNotifications[notifId]) {
                                markNotificationAsRead(notifId);
                              }
                            }
                          };

                          return (
                            <li key={notifId} className="border-b border-gray-800 last:border-none">
                              <button
                                onClick={handleToggle}
                                className={`w-full text-left px-4 py-3 transition-colors flex items-center justify-between gap-3 ${
                                  isSelected ? 'bg-blue-500/10 border-l-4 border-blue-500' : 'hover:bg-gray-700'
                                } ${!isRead ? 'bg-gray-800/40' : ''}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    {!isRead && (
                                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-400" title="Nepročitano" />
                                    )}
                                    <h3 className={`text-sm font-semibold truncate ${isRead ? 'text-gray-300' : 'text-white'}`}>
                                      {notification.title}
                                    </h3>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-gray-400">
                                    {notification.category && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-500/15 text-purple-300">
                                        <BookOpen size={11} />
                                        {notification.category}
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded ${
                                      notification.type === 'info' ? 'bg-blue-500/20 text-blue-300' :
                                      notification.type === 'success' ? 'bg-green-500/20 text-green-300' :
                                      notification.type === 'warning' ? 'bg-yellow-500/20 text-yellow-300' :
                                      'bg-red-500/20 text-red-300'
                                    }`}>
                                      {notification.type === 'success' ? 'novost' : notification.type}
                                    </span>
                                    <span>{formatTimestamp(notification.createdAt)}</span>
                                    {isRead && (
                                      <span className="inline-flex items-center gap-1 text-emerald-400/80">
                                        <CheckCircle2 size={12} />
                                        Pročitano
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <svg
                                  className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isSelected ? 'rotate-180' : ''}`}
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {isSelected && (
                                <div className="px-5 pb-5 bg-gray-900/70">
                                  <article className="bg-gray-900/60 border border-gray-700 rounded-lg overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-700 bg-gray-800/50 flex flex-wrap items-center justify-between gap-2">
                                      <div className="flex items-center gap-2 text-xs text-gray-400">
                                        <Bell size={14} className="text-blue-400" />
                                        <span>SpinTasker administracija</span>
                                        <span>•</span>
                                        <time>{formatTimestamp(notification.createdAt)}</time>
                                      </div>
                                      {notification.category && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-purple-500/15 text-purple-300">
                                          {notification.category}
                                        </span>
                                      )}
                                    </div>
                                    <div className="p-4">
                                      <NotificationMessage message={notification.message} />
                                    </div>
                                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-700 px-4 py-3 bg-gray-800/30">
                                      <div className="flex items-center gap-2">
                                        {!isRead ? (
                                          <button
                                            onClick={() => {
                                              markNotificationAsRead(notifId);
                                              toast.success('Obaveštenje označeno kao pročitano');
                                            }}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                                          >
                                            <CheckCircle2 size={15} />
                                            Označi kao pročitano
                                          </button>
                                        ) : (
                                          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400">
                                            <CheckCircle2 size={15} />
                                            Pročitano
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => {
                                          const updatedHidden = { ...hiddenNotifications, [notifId]: true };
                                          persistHidden(updatedHidden);
                                          setHiddenNotifications(updatedHidden);
                                          toast.info('Obaveštenje je sakriveno iz liste');
                                          setSelectedEntryId(null);
                                        }}
                                        className="px-3 py-1.5 rounded text-sm font-medium text-gray-400 hover:text-gray-200 hover:bg-gray-700/50 transition-colors"
                                      >
                                        Sakrij iz liste
                                      </button>
                                    </div>
                                  </article>
                                </div>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </>
              ) : activeTab === 'comments' ? (
                isSuperAdmin ? (
                  loadingTutorialAdmin ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                    </div>
                  ) : (
                    <div className="px-4 py-4 space-y-10 pb-10 max-w-4xl">
                      <section>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-amber-200">Na čekanju</h3>
                          <span className="text-xs text-gray-500">{tutorialAdmin.pending.length}</span>
                        </div>
                        {tutorialAdmin.pending.length === 0 ? (
                          <p className="text-sm text-gray-500">Nema komentara koji čekaju odobrenje.</p>
                        ) : (
                          <ul className="space-y-3">
                            {tutorialAdmin.pending.map((comment) => (
                              <li
                                key={comment._id}
                                className="rounded-lg border border-amber-900/50 bg-gray-900/60 p-3"
                              >
                                {comment.tutorialTitle ? (
                                  <p className="text-xs text-amber-300/90 mb-1">
                                    Tutorijal: {comment.tutorialTitle}
                                  </p>
                                ) : null}
                                <p className="text-sm text-gray-200 whitespace-pre-wrap">{comment.message}</p>
                                <p className="text-[11px] text-gray-500 mt-2">
                                  {comment.authorName} • {comment.authorEmail} •{' '}
                                  {formatTimestamp(comment.createdAt)}
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleModerateTutorialComment(comment._id, 'approve')}
                                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                                  >
                                    Objavi
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleModerateTutorialComment(comment._id, 'reject')}
                                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-red-600/90 hover:bg-red-600 text-white"
                                  >
                                    Odbij
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>

                      <section>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-emerald-200">Odobreno (javno na Tutorijalima)</h3>
                          <span className="text-xs text-gray-500">{tutorialAdmin.approved.length}</span>
                        </div>
                        {tutorialAdmin.approved.length === 0 ? (
                          <p className="text-sm text-gray-500">Nema odobrenih komentara.</p>
                        ) : (
                          <ul className="space-y-3">
                            {tutorialAdmin.approved.map((comment) => (
                              <li
                                key={comment._id}
                                className="rounded-lg border border-emerald-900/40 bg-gray-900/60 p-3"
                              >
                                {comment.tutorialTitle ? (
                                  <p className="text-xs text-emerald-300/80 mb-1">
                                    Tutorijal: {comment.tutorialTitle}
                                  </p>
                                ) : null}
                                <p className="text-sm text-gray-200 whitespace-pre-wrap">{comment.message}</p>
                                <p className="text-[11px] text-gray-500 mt-2">
                                  {comment.authorName} • {comment.authorEmail} •{' '}
                                  {formatTimestamp(comment.createdAt)}
                                </p>
                                <div className="mt-3">
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTutorialComment(comment._id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-700 hover:bg-red-600/80 text-white border border-gray-600"
                                  >
                                    <Trash2 size={14} />
                                    Obriši sa sajta
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>

                      <section>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-400">Odbijeno</h3>
                          <span className="text-xs text-gray-500">{tutorialAdmin.rejected.length}</span>
                        </div>
                        {tutorialAdmin.rejected.length === 0 ? (
                          <p className="text-sm text-gray-500">Nema odbijenih komentara.</p>
                        ) : (
                          <ul className="space-y-3">
                            {tutorialAdmin.rejected.map((comment) => (
                              <li
                                key={comment._id}
                                className="rounded-lg border border-gray-700 bg-gray-900/40 p-3"
                              >
                                <p className="text-sm text-gray-400 whitespace-pre-wrap line-through opacity-90">
                                  {comment.message}
                                </p>
                                <p className="text-[11px] text-gray-500 mt-2">
                                  {comment.authorName} • {formatTimestamp(comment.createdAt)}
                                </p>
                                <div className="mt-3">
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTutorialComment(comment._id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-700 hover:bg-red-600/80 text-white"
                                  >
                                    <Trash2 size={14} />
                                    Obriši zapis
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    </div>
                  )
                ) : (
                  <p className="p-6 text-gray-400 text-sm">Samo super admin vidi komentare tutorijala.</p>
                )
              ) : (
                // Show local notes
                <>
                  {filteredEntries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-10 px-6">
                      <p className="text-sm">Još uvek nema zapisa u ovoj kategoriji.</p>
                      <p className="text-xs text-gray-500 mt-1">Kliknite na Novo da biste dodali prvi zapis.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-700">
                      {filteredEntries.map((entry) => (
                        <li key={entry.id}>
                          <div className={`px-4 py-3 transition-colors ${entry.id === selectedEntryId ? 'bg-blue-500/10 border-l-4 border-blue-500' : 'hover:bg-gray-700'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <button onClick={() => handleSelect(entry)} className="text-left flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-sm font-semibold text-white truncate">
                                    {entry.title || 'Bez naslova'}
                                  </h3>
                                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">
                                    Interna beleška
                                  </span>
                                </div>
                                {entry.content && (
                                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                                    {entry.content}
                                  </p>
                                )}
                              </button>
                              <div className="flex items-center gap-2">
                                <button
                                  title="Uredi"
                                  onClick={() => handleSelect(entry)}
                                  className="p-1.5 rounded hover:bg-gray-600/50 text-gray-300 hover:text-white"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  title="Obriši"
                                  onClick={() => handleDelete(entry.id)}
                                  className="p-1.5 rounded hover:bg-red-600/20 text-red-400 hover:text-red-300"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            <div className="mt-1 text-xs text-gray-500">{formatTimestamp(entry.updatedAt)}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </aside>

          {activeTab === 'notes' && (
          <section className="lg:col-span-8 bg-gray-800/80 border border-gray-700 rounded-xl shadow-lg p-4 sm:p-6">
            {(selectedEntryId || isCreatingNew) ? (
              <div className="flex flex-col gap-4 h-full">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="text-xs text-gray-400">Kreirano: {formatTimestamp(draft.createdAt)}</p>
                    <p className="text-xs text-gray-500">Ažurirano: {formatTimestamp(draft.updatedAt)}</p>
                  </div>
                  {selectedEntryId && (
                    <button
                      onClick={() => handleDelete(selectedEntryId)}
                      className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                      Obriši
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  value={draft.title || ''}
                  onChange={(event) => handleFieldChange('title', event.target.value)}
                  placeholder="Naslov beleške"
                  className="w-full bg-gray-900/80 border border-gray-700 rounded-lg px-3 py-2 text-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <textarea
                  value={draft.content || ''}
                  onChange={(event) => handleFieldChange('content', event.target.value)}
                  placeholder="Sadržaj beleške..."
                  rows={10}
                  className="flex-1 bg-gray-900/80 border border-gray-700 rounded-lg px-3 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="flex justify-end gap-2">
                  {isCreatingNew && (
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Otkaži
                    </button>
                  )}
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Save size={16} />
                    Sačuvaj
                  </button>
                </div>
              </div>
            ) : (
              // Show empty state for notes tab
              <div className="flex flex-col items-center justify-center text-center text-gray-400 h-full py-10">
                <p className="text-sm">
                  {filteredEntries.length === 0
                    ? 'Još uvek nema zapisa. Kliknite na "Novo" da biste dodali prvi zapis.'
                    : 'Izaberite zapis iz liste ili dodajte novi kako biste ga uredili.'}
                </p>
                <button
                  onClick={handleCreate}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Plus size={16} />
                  Dodaj zapis
                </button>
              </div>
            )}
          </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsView;

